import * as THREE from "three";

export function applyTextureByMaterialName(scene, matName, texture, overrides = {}) {
  texture.colorSpace = THREE.SRGBColorSpace;

  scene.traverse((child) => {
    if (!child.isMesh) return;

    const applyToMat = (m) => {
      if (m.name !== matName) return m;

      const t = texture.clone();
      t.needsUpdate = true;
      if (m.map) {
        t.wrapS = m.map.wrapS;
        t.wrapT = m.map.wrapT;
        t.repeat.copy(m.map.repeat);
        t.offset.copy(m.map.offset);
        t.rotation = m.map.rotation;
        t.center.copy(m.map.center);
        t.updateMatrix();
      } else {
        t.wrapS = THREE.RepeatWrapping;
        t.wrapT = THREE.RepeatWrapping;
      }

      if (overrides.lambert) {
        const c = new THREE.MeshStandardMaterial({
          map: t,
          color: 0xffffff,
          roughness: 0.85,
          metalness: 0.05,
          envMap: overrides.envMap ?? null,
          envMapIntensity: 0,
        });
        c.name = m.name;
        c.needsUpdate = true;
        return c;
      }

      const c = m.clone();
      c.map = t;
      c.color.set(0xffffff);
      if (overrides.roughness !== undefined) c.roughness = overrides.roughness;
      if (overrides.metalness !== undefined) c.metalness = overrides.metalness;
      if (overrides.envMapIntensity !== undefined) c.envMapIntensity = overrides.envMapIntensity;
      c.needsUpdate = true;
      return c;
    };

    child.material = Array.isArray(child.material)
      ? child.material.map(applyToMat)
      : applyToMat(child.material);
  });
}

export function applyColorByMaterialName(scene, matName, { hex, roughness, metalness }) {
  const color = new THREE.Color(hex);

  scene.traverse((child) => {
    if (!child.isMesh) return;

    const applyToMat = (m) => {
      if (m.name !== matName) return m;
      const c = m.clone();
      c.map = null;
      c.color.copy(color);
      c.roughness = roughness;
      c.metalness = metalness;
      c.needsUpdate = true;
      return c;
    };

    child.material = Array.isArray(child.material)
      ? child.material.map(applyToMat)
      : applyToMat(child.material);
  });
}

export function tuneGlassOpacity(scene, opacity) {
  scene.traverse((child) => {
    if (!child.isMesh) return;

    const applyToMat = (m) => {
      if (!m.name.toLowerCase().includes("glass")) return m;
      m.opacity = opacity;
      m.needsUpdate = true;
      return m;
    };

    child.material = Array.isArray(child.material)
      ? child.material.map(applyToMat)
      : applyToMat(child.material);
  });
}

const ENV_MAP_EXEMPT_NAMES = ["glass", "hinge", "hanger"];

export function disableEnvMapExceptGlass(scene, envTexture) {
  scene.traverse((child) => {
    if (!child.isMesh) return;

    const applyToMat = (m) => {
      const name = m.name.toLowerCase();
      if (ENV_MAP_EXEMPT_NAMES.some((exempt) => name.includes(exempt))) return m;
      m.envMap = envTexture ?? null;
      m.envMapIntensity = 0;
      m.needsUpdate = true;
      return m;
    };

    child.material = Array.isArray(child.material)
      ? child.material.map(applyToMat)
      : applyToMat(child.material);
  });
}
